import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';
import { applyBrandTheme, BRAND_THEMES, DEFAULT_THEME_ID } from '../brand-theme';
import { applyTenantHeadBranding } from '../tenant-head';

const TenantBrandingContext = createContext({
  display_name: '',
  logo_url: null,
  theme: DEFAULT_THEME_ID,
  loading: true,
});

export const useTenantBranding = () => useContext(TenantBrandingContext);

export const TenantBrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    display_name: '',
    logo_url: null,
    app_icon_url: null,
    theme: DEFAULT_THEME_ID,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const report = (kind, extra) => {
      try { if (window.__bs_report) window.__bs_report(Object.assign({ kind }, extra || {})); } catch (_) {}
    };

    const load = async () => {
      report('TENANT_CONFIG_START');
      try {
        // Hard 8s timeout so a stalled branding request can never blank the app.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        const res = await api.get('/api/tenant/branding', { signal: controller.signal });
        clearTimeout(timeoutId);
        if (cancelled) return;
        const name = res.data.display_name || '';
        const theme = res.data.theme || DEFAULT_THEME_ID;
        applyBrandTheme(theme);
        setBranding({
          display_name: name,
          logo_url: res.data.logo_url,
          app_icon_url: res.data.app_icon_url,
          theme,
          loading: false,
        });
        report('TENANT_CONFIG_SUCCESS', { display_name: name, theme });

        try {
          const themeDef = BRAND_THEMES[theme] || BRAND_THEMES[DEFAULT_THEME_ID];
          applyTenantHeadBranding({
            display_name: name,
            logo_url: res.data.logo_url,
            app_icon_url: res.data.app_icon_url,
            branding_updated_at: res.data.branding_updated_at,
            theme_primary: themeDef.primary,
          });
        } catch (e) {
          report('TENANT_HEAD_BRANDING_THREW', { message: String(e && (e.message || e)) });
        }
      } catch (e) {
        report('TENANT_CONFIG_FAILED', { message: String(e && (e.message || e)) });
        if (!cancelled) setBranding(prev => ({ ...prev, loading: false }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <TenantBrandingContext.Provider value={branding}>
      {children}
    </TenantBrandingContext.Provider>
  );
};
